import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowUpRight } from "lucide-react";
import { useLang } from "../contexts/LanguageContext";
import Reveal from "../components/Reveal";
import MagneticButton from "../components/MagneticButton";

const Pricing = () => {
  const { t } = useLang();
  const [annual, setAnnual] = useState(false);

  return (
    <div data-testid="pricing-page">
      <section className="section-pad" style={{ paddingTop: "clamp(160px, 18vw, 220px)" }}>
        <div className="font-mono line-deco" style={{ fontSize: "0.75rem", letterSpacing: "0.2em", color: "var(--gold)", textTransform: "uppercase", marginBottom: 32 }}>
          {t.pricing.label}
        </div>
        <h1 className="font-display" style={{ fontSize: "clamp(3rem, 8vw, 7rem)", fontWeight: 300, lineHeight: 1, color: "var(--warm-off-white)", letterSpacing: "-0.02em" }}>
          {t.pricing.title}
        </h1>
        <p className="font-body" style={{ color: "rgba(248,244,240,0.65)", lineHeight: 1.7, marginTop: 24, fontSize: "1.1rem", maxWidth: 640 }}>
          {t.pricing.intro}
        </p>

        <div className="flex items-center" style={{ gap: 12, marginTop: 48 }}>
          <span className="font-mono" style={{ color: !annual ? "var(--gold)" : "rgba(248,244,240,0.5)", fontSize: "0.75rem", letterSpacing: "0.18em", textTransform: "uppercase" }}>{t.pricing.monthly}</span>
          <button data-testid="pricing-toggle" onClick={() => setAnnual((a) => !a)} aria-label="toggle billing" style={{ width: 56, height: 28, background: "var(--rich-black-light)", border: "1px solid rgba(201,151,58,0.4)", borderRadius: 999, position: "relative", cursor: "pointer", padding: 0 }}>
            <span style={{ position: "absolute", top: 2, left: annual ? 28 : 2, width: 22, height: 22, background: "var(--gold)", borderRadius: "50%", transition: "left 0.4s var(--ease-cinema)" }} />
          </button>
          <span className="font-mono" style={{ color: annual ? "var(--gold)" : "rgba(248,244,240,0.5)", fontSize: "0.75rem", letterSpacing: "0.18em", textTransform: "uppercase" }}>{t.pricing.annual}</span>
          {annual && <span className="font-mono" style={{ marginLeft: 12, color: "var(--gold-light)", fontSize: "0.75rem", letterSpacing: "0.15em" }}>{t.pricing.annualDiscount}</span>}
        </div>
      </section>

      <section className="section-pad" style={{ paddingTop: 0 }}>
        <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 24 }}>
          {t.pricing.tiers.map((tier, i) => {
            const popular = i === 1;
            const price = annual ? tier.price.annual : tier.price.monthly;
            return (
              <Reveal key={tier.name} delay={i * 100}>
                <div data-testid={`pricing-tier-${tier.name.toLowerCase()}`} style={{
                  position: "relative", padding: "48px 32px",
                  background: popular ? "linear-gradient(180deg, rgba(139,32,53,0.25), rgba(17,17,17,0.95))" : "var(--rich-black-light)",
                  border: popular ? "1px solid var(--gold)" : "1px solid rgba(139,32,53,0.25)",
                  display: "flex", flexDirection: "column", height: "100%",
                }}>
                  {popular && (
                    <div style={{ position: "absolute", top: -1, right: -1, background: "var(--gold)", color: "var(--rich-black)", padding: "6px 14px", fontSize: "0.65rem", letterSpacing: "0.2em", textTransform: "uppercase", fontFamily: "Space Mono" }}>
                      {t.pricing.popular}
                    </div>
                  )}
                  <h3 className="font-display" style={{ fontSize: "2rem", fontWeight: 400, color: "var(--warm-off-white)" }}>{tier.name}</h3>
                  <p style={{ color: "rgba(248,244,240,0.6)", marginTop: 8, minHeight: 48 }}>{tier.desc}</p>
                  <div style={{ marginTop: 24, marginBottom: 32, display: "flex", alignItems: "baseline", gap: 8 }}>
                    <span className="font-display" style={{ fontSize: "clamp(2.75rem, 5vw, 4.25rem)", fontWeight: 300, color: "var(--gold-light)", lineHeight: 1 }}>${price.toLocaleString()}</span>
                    <span className="font-mono" style={{ color: "rgba(248,244,240,0.55)", fontSize: "0.8rem" }}>/ {annual ? "yr" : "mo"}</span>
                  </div>
                  <ul style={{ listStyle: "none", padding: 0, display: "flex", flexDirection: "column", gap: 14, flex: 1 }}>
                    {tier.features.map((f, k) => (
                      <li key={k} style={{ display: "flex", alignItems: "flex-start", gap: 12, color: "rgba(248,244,240,0.78)", lineHeight: 1.5 }}>
                        <Check size={16} color="var(--gold)" style={{ marginTop: 4, flexShrink: 0 }} /> <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/contact" data-testid={`pricing-cta-${tier.name.toLowerCase()}`} style={{ textDecoration: "none", marginTop: 32 }}>
                    <MagneticButton className="font-mono" style={{ width: "100%", padding: "16px 24px", background: popular ? "var(--gold)" : "transparent", color: popular ? "var(--rich-black)" : "var(--warm-off-white)", border: popular ? "none" : "1px solid var(--warm-off-white)", textTransform: "uppercase", letterSpacing: "0.2em", fontSize: "0.7rem", cursor: "pointer" }}>
                      {t.pricing.cta} <ArrowUpRight size={14} style={{ marginLeft: 8 }} />
                    </MagneticButton>
                  </Link>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal>
          <div style={{ marginTop: 96, padding: "56px 48px", background: "linear-gradient(135deg, var(--bordeaux-dark), var(--rich-black-light))", border: "1px solid rgba(201,151,58,0.3)", display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 32 }} className="corner-deco">
            <div style={{ maxWidth: 640 }}>
              <h3 className="font-display" style={{ fontSize: "clamp(1.75rem, 3vw, 2.75rem)", fontWeight: 300, color: "var(--warm-off-white)" }}>{t.pricing.customTitle}</h3>
              <p style={{ color: "rgba(248,244,240,0.7)", marginTop: 12, lineHeight: 1.7 }}>{t.pricing.customText}</p>
            </div>
            <Link to="/contact" style={{ textDecoration: "none" }}>
              <MagneticButton className="font-mono" style={{ padding: "18px 36px", background: "var(--warm-off-white)", color: "var(--rich-black)", border: "none", textTransform: "uppercase", letterSpacing: "0.2em", fontSize: "0.75rem", cursor: "pointer" }}>
                {t.pricing.customCta} <ArrowUpRight size={14} style={{ marginLeft: 8 }} />
              </MagneticButton>
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default Pricing;
